import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Phone,
  WhatsappLogo,
  Trash,
  X,
  Lock,
  PaperPlaneTilt,
  CheckSquare,
  Square,
  CaretDown,
  CaretRight,
  FolderSimple,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import formatChatTime from '../utils/formatChatTime';
import SettingsHub from '../components/SettingsHub';
import ClientsDirectorySection from '../components/ClientsDirectorySection';
import AppearanceSettings from '../components/AppearanceSettings';
import StatusManagement from '../components/StatusManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Point 48 — the Calling Handler's ONE settings power: full phone number
 * vs last-4-digits.
 * Point 49 — the "Reports" section: a live-filling list of every call/
 * WhatsApp tap, a description box per entry, select + "Send to Admin",
 * password-gated delete of an unsent entry, and a read-only list of
 * reports already sent.
 */
const CallingHandlerSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activities, setActivities] = useState([]);
  const [sentActivities, setSentActivities] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [notes, setNotes] = useState({}); // id -> draft text
  const [sending, setSending] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [showSent, setShowSent] = useState(false);
  const [collapsedFolders, setCollapsedFolders] = useState(() => new Set());
  // Granted "Settings Powers" (admin toggles from Handler Management)
  const [perms, setPerms] = useState(user?.permissions || null);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API}/users/handler/${user.id}/permissions`)
      .then((r) => setPerms(r.data?.permissions || {}))
      .catch(() => setPerms(user?.permissions || {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Issue 4 — group unsent activities into date folders (Today / Yesterday /
  // dated), newest first, for Select-All + folder-or-single send.
  const dayLabel = (iso) => {
    if (!iso) return 'Unknown date';
    const d = new Date(iso);
    const today = new Date();
    const yest = new Date(); yest.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yest.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });
  };
  const folders = useMemo(() => {
    const map = {};
    activities.forEach((a) => {
      const key = (a.created_at || '').slice(0, 10) || 'unknown';
      if (!map[key]) map[key] = { key, label: dayLabel(a.created_at), items: [] };
      map[key].items.push(a);
    });
    return Object.values(map).sort((x, y) => (x.key < y.key ? 1 : -1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities]);

  const allIds = useMemo(() => activities.map((a) => a.id), [activities]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  const toggleFolderSelect = (items) => setSelected((prev) => {
    const next = new Set(prev);
    const all = items.every((i) => next.has(i.id));
    items.forEach((i) => { if (all) next.delete(i.id); else next.add(i.id); });
    return next;
  });
  const toggleFolderCollapse = (key) => setCollapsedFolders((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const loadUnsent = async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/calling-handler/${user.id}/activities`, { params: { sent: false } });
      setActivities(res.data?.activities || []);
    } catch (e) { /* silent — this list polls */ }
  };

  const loadSent = async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/calling-handler/${user.id}/activities`, { params: { sent: true } });
      setSentActivities(res.data?.activities || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadUnsent();
    // Point 49 — "Reports section... fills live" as new taps happen from
    // the dashboard. Polling mirrors the app's existing badge-poll idiom
    // (e.g. InstantAccessPW.jsx polls every 20s).
    const t = setInterval(loadUnsent, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (showSent) loadSent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSent, user?.id]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const saveNote = async (id) => {
    const note = notes[id];
    if (note === undefined) return;
    try {
      await axios.put(`${API}/calling-handler/activity/${id}/note`, { handler_id: user.id, note });
      setActivities((prev) => prev.map((a) => (a.id === id ? { ...a, note } : a)));
    } catch (e) {
      toast.error('Failed to save note');
    }
  };

  const sendIds = async (ids, label) => {
    if (!ids || ids.length === 0) { toast.error('Select at least one activity'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/calling-handler/send-report`, {
        handler_id: user.id,
        activity_ids: ids,
        range_label: label,
      });
      toast.success(`Report sent (${ids.length} activit${ids.length === 1 ? 'y' : 'ies'})`);
      setSelected(new Set());
      loadUnsent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const sendReport = async () => {
    const ids = [...selected];
    if (ids.length === 0) { toast.error('Select at least one activity'); return; }
    const dates = ids
      .map((id) => activities.find((a) => a.id === id)?.created_at)
      .filter(Boolean)
      .map((d) => d.slice(0, 10));
    const uniqueDates = [...new Set(dates)];
    const rangeLabel = uniqueDates.length <= 1 ? (uniqueDates[0] || 'Today') : `${uniqueDates[0]} to ${uniqueDates[uniqueDates.length - 1]}`;
    await sendIds(ids, rangeLabel);
  };

  const sendFolder = async (folder) => {
    await sendIds(folder.items.map((i) => i.id), folder.label);
  };

  const submitDelete = async (e) => {
    e?.preventDefault?.();
    if (!deleteTarget) return;
    if (!deletePassword.trim()) { toast.error('Enter your password'); return; }
    setDeleteBusy(true);
    try {
      await axios.delete(`${API}/calling-handler/activity/${deleteTarget.id}`, {
        params: { handler_id: user.id, password: deletePassword },
      });
      toast.success('Activity deleted');
      setDeleteTarget(null);
      setDeletePassword('');
      loadUnsent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  };

  const renderActivityRow = (a, { selectable }) => (
    <div key={a.id} className="bg-white rounded-lg border border-gray-200 p-3" data-testid={`calling-activity-row-${a.id}`}>
      <div className="flex items-start gap-2">
        {selectable && (
          <button onClick={() => toggleSelect(a.id)} className="mt-0.5 flex-shrink-0" data-testid={`calling-activity-select-${a.id}`}>
            {selected.has(a.id) ? <CheckSquare size={20} weight="fill" className="text-[#008069]" /> : <Square size={20} className="text-gray-300" />}
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {a.kind === 'whatsapp'
              ? <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold"><WhatsappLogo size={14} weight="fill" /> WhatsApp</span>
              : <span className="inline-flex items-center gap-1 text-blue-600 text-xs font-semibold"><Phone size={14} weight="fill" /> Call</span>}
            <span className="font-semibold text-sm text-gray-800">{a.client_snapshot?.name}</span>
            <span className="text-xs text-gray-500">{a.client_snapshot?.phone}</span>
            {a.client_snapshot?.mb_setup && <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-1.5 rounded">{a.client_snapshot.mb_setup}</span>}
            <span className="text-[11px] text-gray-400 ml-auto">{formatChatTime(a.created_at)}</span>
          </div>
          {selectable ? (
            <input
              type="text"
              defaultValue={a.note || ''}
              onChange={(e) => setNotes((prev) => ({ ...prev, [a.id]: e.target.value }))}
              onBlur={() => saveNote(a.id)}
              placeholder="Add a note (e.g. call not answered)…"
              className="w-full mt-1.5 text-xs px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:border-[#008069]"
              data-testid={`calling-activity-note-${a.id}`}
            />
          ) : (
            a.note && <p className="text-xs text-gray-600 mt-1">{a.note}</p>
          )}
        </div>
        {selectable && (
          <button
            onClick={() => { setDeleteTarget(a); setDeletePassword(''); }}
            className="p-1.5 text-red-500 hover:bg-red-50 rounded-full flex-shrink-0"
            title="Delete (password required)"
            data-testid={`calling-activity-delete-btn-${a.id}`}
          >
            <Trash size={16} />
          </button>
        )}
      </div>
    </div>
  );

  const I = SettingsHub.icons;
  const allow = (key) => !!(perms || {})[key];
  const powerSections = [];
  if (allow('client_list')) {
    powerSections.push({
      key: 'client_list', title: 'Client List',
      subtitle: 'Open the full Client Management screen (add / edit / delete)',
      icon: I.Users, accent: '#0ea5e9',
      render: () => (
        <div className="p-4">
          <button onClick={() => navigate('/client-list', { state: { from: '/calling-handler-settings' } })} className="w-full py-2.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold" data-testid="calling-open-client-management-btn">Open Client Management →</button>
        </div>
      ),
    });
  }
  if (allow('client_directory')) {
    powerSections.push({
      key: 'clients', title: 'Clients Directory',
      subtitle: 'View all clients by MB, message on WhatsApp, open chats',
      icon: I.UsersFour, accent: '#2563eb',
      render: () => <ClientsDirectorySection actorRole={user.role} permissions={perms} />,
    });
  }
  if (allow('create_client')) {
    powerSections.push({
      key: 'create_client', title: 'Create Client',
      subtitle: 'Add a new customer account (same flow as admin)',
      icon: I.IdentificationCard, accent: '#0d9488',
      render: () => (
        <div className="p-4">
          <button onClick={() => navigate('/handler/create-client', { state: { from: '/calling-handler-settings' } })} className="w-full py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold" data-testid="calling-open-create-client-btn">Open Create Client →</button>
        </div>
      ),
    });
  }
  if (allow('status')) {
    powerSections.push({
      key: 'status', title: 'My Status',
      subtitle: 'Update your handler activity status',
      icon: I.PaintBrush, accent: '#16a34a',
      render: () => <StatusManagement user={user} />,
    });
  }
  if (allow('appearance')) {
    powerSections.push({
      key: 'appearance', title: 'Appearance',
      subtitle: 'Theme (5 presets) and font size for this device',
      icon: I.PaintBrush, accent: '#7c3aed',
      render: () => <AppearanceSettings />,
    });
  }
  if (allow('broadcast')) {
    powerSections.push({
      key: 'broadcast', title: 'Broadcast Message',
      subtitle: 'Push a single message to every customer or a chosen MB',
      icon: I.MegaphoneSimple, accent: '#ea580c',
      render: () => (
        <div className="p-4">
          <button onClick={() => navigate('/broadcast', { state: { from: '/calling-handler-settings' } })} className="w-full py-2.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold" data-testid="calling-open-broadcast-btn">Open Broadcast Composer →</button>
        </div>
      ),
    });
  }
  if (allow('service_timeline')) {
    powerSections.push({
      key: 'service_timeline', title: 'Service Timelines',
      subtitle: 'Track deposit / withdraw / support requests & timings',
      icon: I.PaintBrush, accent: '#0891b2',
      render: () => (
        <div className="p-4">
          <button onClick={() => navigate('/handler/service-timelines')} className="w-full py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold" data-testid="calling-open-service-timelines-btn">Open Service Timelines →</button>
        </div>
      ),
    });
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="calling-handler-settings">
      <div className="bg-[#008069] text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate('/calling-handler-dashboard')} className="p-1.5 hover:bg-white/10 rounded-full" data-testid="calling-settings-back-btn">
          <ArrowLeft size={22} />
        </button>
        <h1 className="text-lg font-semibold">Settings</h1>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Settings Powers — admin-granted capabilities (Client List, Directory,
            Create Client, Appearance, Broadcast, Status, Service Timelines) */}
        {powerSections.length > 0 && (
          <div data-testid="calling-settings-powers">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">Your Powers</h2>
            <SettingsHub sections={powerSections} />
          </div>
        )}
        {/* Reports */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Reports</h2>
            <button
              onClick={sendReport}
              disabled={sending || selected.size === 0}
              className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#008069] text-white disabled:opacity-40 flex items-center gap-1.5"
              data-testid="calling-settings-send-report-btn"
            >
              <PaperPlaneTilt size={14} weight="fill" /> Send to Admin ({selected.size})
            </button>
          </div>
          {activities.length === 0 ? (
            <p className="text-xs text-gray-400 py-4 text-center">No unsent activity yet — call/WhatsApp taps from the Client Directory show up here live.</p>
          ) : (
            <div className="space-y-3">
              {/* Select-All bar */}
              <button
                onClick={toggleSelectAll}
                className="w-full flex items-center gap-2 text-xs font-semibold text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-100"
                data-testid="calling-select-all-btn"
              >
                {allSelected ? <CheckSquare size={18} weight="fill" className="text-[#008069]" /> : <Square size={18} className="text-gray-400" />}
                Select All ({allIds.length})
              </button>

              {/* Date folders */}
              {folders.map((folder) => {
                const collapsed = collapsedFolders.has(folder.key);
                const folderAllSelected = folder.items.every((i) => selected.has(i.id));
                return (
                  <div key={folder.key} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`calling-folder-${folder.key}`}>
                    <div className="flex items-center gap-2 bg-gray-50 px-3 py-2">
                      <button onClick={() => toggleFolderCollapse(folder.key)} className="flex-shrink-0" data-testid={`calling-folder-toggle-${folder.key}`}>
                        {collapsed ? <CaretRight size={16} className="text-gray-500" /> : <CaretDown size={16} className="text-gray-500" />}
                      </button>
                      <button onClick={() => toggleFolderSelect(folder.items)} className="flex-shrink-0" data-testid={`calling-folder-select-${folder.key}`}>
                        {folderAllSelected ? <CheckSquare size={18} weight="fill" className="text-[#008069]" /> : <Square size={18} className="text-gray-400" />}
                      </button>
                      <FolderSimple size={16} weight="fill" className="text-amber-500 flex-shrink-0" />
                      <span className="text-xs font-bold text-gray-700 flex-1">{folder.label} <span className="font-normal text-gray-400">({folder.items.length})</span></span>
                      <button
                        onClick={() => sendFolder(folder)}
                        disabled={sending}
                        className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#008069]/10 text-[#008069] disabled:opacity-40 flex items-center gap-1"
                        data-testid={`calling-folder-send-${folder.key}`}
                      >
                        <PaperPlaneTilt size={12} weight="fill" /> Send folder
                      </button>
                    </div>
                    {!collapsed && (
                      <div className="p-2 space-y-2">
                        {folder.items.map((a) => renderActivityRow(a, { selectable: true }))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sent history (read-only) */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <button
            onClick={() => setShowSent((v) => !v)}
            className="w-full text-left text-sm font-bold text-gray-700 uppercase tracking-wide"
            data-testid="calling-settings-toggle-sent"
          >
            {showSent ? 'Hide' : 'Show'} Sent Activity ({sentActivities.length || '...'})
          </button>
          {showSent && (
            sentActivities.length === 0 ? (
              <p className="text-xs text-gray-400 py-4 text-center">Nothing sent yet.</p>
            ) : (
              <div className="space-y-2 mt-2">
                {sentActivities.map((a) => renderActivityRow(a, { selectable: false }))}
              </div>
            )
          )}
        </div>
      </div>

      {/* Delete confirmation — handler's own password */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black/70 z-[90] flex items-center justify-center p-3"
          onClick={(e) => { if (e.target === e.currentTarget) { setDeleteTarget(null); setDeletePassword(''); } }}
        >
          <form onSubmit={submitDelete} className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-red-100 text-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash size={18} weight="fill" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-red-800 text-sm">Delete this activity?</div>
                <div className="text-[11px] text-gray-600">{deleteTarget.client_snapshot?.name} · unsent only</div>
              </div>
              <button type="button" onClick={() => { setDeleteTarget(null); setDeletePassword(''); }} className="p-1 hover:bg-gray-100 rounded">
                <X size={16} />
              </button>
            </div>
            <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
              <Lock size={10} /> Confirm with your password
            </label>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              autoFocus
              disabled={deleteBusy}
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
              placeholder="Your login password"
              data-testid="calling-activity-delete-password"
            />
            <div className="flex gap-2 mt-4">
              <button type="button" onClick={() => { setDeleteTarget(null); setDeletePassword(''); }} disabled={deleteBusy} className="flex-1 py-2 rounded border border-gray-300 text-sm font-semibold hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={deleteBusy || !deletePassword.trim()} className="flex-1 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50" data-testid="calling-activity-delete-confirm">
                {deleteBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CallingHandlerSettings;
